import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import merge from 'lodash.merge'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import Head from 'next/head'

import {
  ProductPageData,
  ProductPageVariables,
} from '@thg-commerce/enterprise-aurora-product/resolvers/ProductPage/types'
import {
  generateObjectCacheKey,
  ObjectCacheKey,
  pageTTLConfig,
  PageType as CachePageType,
} from '@thg-commerce/enterprise-cache'
import {
  EnterpriseNextPage,
  getSafeUrlParametersFromReq,
  getSafeUrlParametersFromWindow,
  i18n,
  PageWrapper,
  Routes,
  useSiteConfig,
  useSiteDefinition,
  useTheme,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { createGraphQLClient } from '@thg-commerce/enterprise-core/src/Factory/graphql'
import { loadSiteProperties } from '@thg-commerce/enterprise-core/src/i18n/siteProperties'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import { getHistogram } from '@thg-commerce/enterprise-metrics/src/prometheus/histogram'
import {
  ComponentName,
  ComponentWidgetsVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/ComponentWidgets'
import {
  AlternateLink,
  Country,
  Currency,
  Feature,
  Maybe,
  ProductRecommendationType,
  QueryPageArgs,
  Widget,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { VariantChoices } from '@thg-commerce/enterprise-network/src/transformers/product/options'
import {
  loadPageTheme,
  PageTheme,
  ProductPageLayout,
} from '@thg-commerce/enterprise-pages'
import { ProductOptionsLink } from '@thg-commerce/enterprise-product-options/src/components/ProductOptionsLink'
import { getSelectedOptions } from '@thg-commerce/enterprise-product-options/src/utils/options'
import { ThemeProvider } from '@thg-commerce/enterprise-theme'
import { isClickAndCollectEnabled } from '@thg-commerce/enterprise-utils/src/ClickAndCollect/ClickAndCollect'
import { BackwardsNavigation, Separator } from '@thg-commerce/gravity-elements'
import { GridItem } from '@thg-commerce/gravity-system'
import { mq, spacing, styled, Text } from '@thg-commerce/gravity-theme'

import {
  OtherCustomersBought as OtherCustomersBoughtComponent,
  PaginatedProductReviews,
  ProductBlock,
} from '../../../src/components'
import { getReviewFiltersFromUrl } from '../../../src/components/ProductReviews/utils'
import { ProductOptions } from '../../../src/components/reviews/ProductOptions'
import { ComponentWidgets as WIDGET_QUERY } from '../../../src/ComponentWidgets.graphql'
import { ReviewsPageContextProvider } from '../../../src/context/ReviewsPageContext'
import { ReviewsQuery as REVIEWS_PAGE_QUERY_AURORA } from '../../../src/graphql/ReviewsPageQuery.graphql'
import { getReviewPageParams } from '../../../src/utils/pageParams'
import CustomError from '../../_error'

type ReviewsPage = EnterpriseNextPage<{
  pageTheme?: PageTheme<ProductPageLayout> | null
  product?: ProductPageData
  options?: VariantChoices
  pageParams?: {
    pageNumber?: string
    sortType?: string
    reviewFilters?: string
  }
  siteName: string
  notFoundPageWidgets?: Widget[]
  preselectVariant?: boolean
}>

const MAX_REVIEWS_PER_PAGE = 10

const OtherCustomersBought = withHydrationOnDemand({ on: [['idle']] })(
  OtherCustomersBoughtComponent,
)

const StyledGrid = styled(PageWrapper)`
  max-width: ${(props) => props.theme.site.siteWidth};
`

const StyledBackwardsNavigation = styled(BackwardsNavigation)`
  padding: 0 0 ${spacing(2)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(2)};
  }
`

const StyledReviewTitle = styled.h2`
  ${Text('large2', 'alternate')}
  padding: ${spacing(3)} 0;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    ${Text('xlarge1', 'alternate')}
    padding: ${spacing(4)} 0;
  }
`

const ProductBlockGridItem = styled(GridItem)`
  display: none;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: flex;
    flex-direction: column;
  }
`

const ProductReview: ReviewsPage = (props) => {
  const existingParams = { ...props.pageParams }
  const theme = useTheme()
  const { disableClickOnProductTitleReviewPage } = useSiteConfig()

  if (typeof window !== 'undefined') {
    const params = getSafeUrlParametersFromWindow(window, '*')

    for (const [key, value] of Object.entries(params)) {
      existingParams[key] = value
    }
  }

  const { domain, originUrl } = useSiteDefinition()
  const i18nText = {
    backToProductText: i18n(
      'reviews.paginatedproductreviews.backtoproductlink.text',
    ),
    paginatedReviewsTitle: i18n('reviews.paginatedproductreviews.title'),
    customerReviewsTitle: i18n('reviews.customerreviews.title'),
    metaTagKeywordsEnd: i18n('meta.keywords.end.text'),
    siteTitleEnd: i18n('titles.end.text'),
    siteTitleReviews: i18n('reviews.reviews.label'),
    metaDescrPart1: i18n('meta.description.product.1.text'),
    metaDescrPart2: i18n('meta.description.product.2.text'),
    metaDescrPart3: i18n('meta.description.product.3.text'),
  }

  if (!props.product) {
    return (
      <CustomError
        statusCode={404}
        componentWidgets={props.notFoundPageWidgets}
      />
    )
  }
  const totalPages = props.product.reviews?.totalPages || 1

  const metaDescription = props.product.metaDescription
    ? props.product.metaDescription
    : `${i18nText.metaDescrPart1} ${props.product?.title} ${i18nText.metaDescrPart2} ${i18nText.metaDescrPart3}`.trim()

  const pageNumber = parseInt(props.pageParams?.pageNumber || '1', 10)
  const sortParam = props.pageParams?.sortType
    ? `&sortType=${props.pageParams.sortType}`
    : ''
  const canonicalUrl = `https://${domain}${props.product.url.replace(
    '.html',
    '.reviews',
  )}`
  const urlWithQuery = `${canonicalUrl}?pageNumber=`

  return (
    <React.Fragment>
      <Head>
        <title>
          {props.product.title} {i18nText.siteTitleReviews}{' '}
          {i18nText.siteTitleEnd}
        </title>
        <meta name="description" content={metaDescription} />
        {pageNumber > 1 && (
          <link
            data-testid="reviews-link-prev"
            rel="prev"
            href={`${urlWithQuery}${pageNumber - 1}${sortParam}`}
          />
        )}
        {totalPages > 1 && pageNumber < totalPages && (
          <link
            data-testid="reviews-link-next"
            rel="next"
            href={`${urlWithQuery}${pageNumber + 1}${sortParam}`}
          />
        )}
        {props.product.alternateLinks.map((alternate: AlternateLink, i) => {
          return (
            <link
              key={i}
              rel="alternate"
              href={`${alternate.url.replace('.html', '.reviews')}`}
              hrefLang={alternate.locale}
            />
          )
        })}
        <ProductOptionsLink originUrl={originUrl} sku={props.product.sku} />
      </Head>
      <ThemeProvider
        theme={merge(theme, { pageTheme: props.pageTheme?.theme })}
      >
        <ReviewsPageContextProvider
          product={props.product}
          options={props.options || {}}
          preselectVariant={props.preselectVariant ?? true}
        >
          <StyledGrid
            columns={12}
            paddingVertical={2}
            paddingHorizontal={[2, 2, 2, 4]}
            compactMargin={true}
          >
            <GridItem colSpan={12}>
              <StyledBackwardsNavigation
                text={i18nText.backToProductText}
                url={props.product.url}
              />
              <Separator />
            </GridItem>

            <React.Fragment>
              <GridItem colSpan={12}>
                <StyledReviewTitle
                  aria-label={`${i18nText.paginatedReviewsTitle}, ${props.product.title}`}
                  data-testid="reviews-heading-level-2"
                >
                  {i18nText.paginatedReviewsTitle}
                </StyledReviewTitle>
                <Separator />
              </GridItem>
              <GridItem colSpan={[12, 12, 7, 7]}>
                <PaginatedProductReviews
                  customerReviewsTitle={i18nText.customerReviewsTitle}
                  {...props.pageParams}
                />
              </GridItem>
              <ProductBlockGridItem
                colSpan={[0, 0, 5, 5]}
                colStart={[0, 0, 8, 8]}
              >
                <ProductBlock
                  {...(!disableClickOnProductTitleReviewPage && {
                    url: props.product.url,
                  })}
                  productName={props.product.title}
                  customerReviewsTitle={i18nText.customerReviewsTitle}
                />
                <ProductOptions />
                <OtherCustomersBought
                  product={props.product}
                  desktopWrap={true}
                  trackingContext={RecommendationTrackingContext.REVIEW}
                />
              </ProductBlockGridItem>
            </React.Fragment>
          </StyledGrid>
        </ReviewsPageContextProvider>
      </ThemeProvider>
    </React.Fragment>
  )
}

ProductReview.Layout = FullHeaderLayout

ProductReview.supportsConcessions = true

export const getServerSideProps = withCacheConfiguration<
  { props: ReviewsPage },
  GetServerSidePropsContext
>(async (context) => {
  const results = getSafeUrlParametersFromReq<{
    sku: string
    pageNumber?: string
    sortType?: string
  }>(context, ['sku', 'pageNumber', 'sortType'])

  const { skuNumber, offset, sort } = getReviewPageParams(
    results,
    MAX_REVIEWS_PER_PAGE,
  )
  const nextConfig = getConfig() as NextConfig
  const request = context.req as EnterpriseRequest
  const config = await loadConfiguration(nextConfig, request)
  const { publicRuntimeConfig } = config
  const {
    siteDefinition,
    siteConfig,
    shippingDestination: defaultShippingDesintation,
  } = publicRuntimeConfig

  const { reviewFilters } = request.query
  const reviewScoreFilters = decodeURIComponent(reviewFilters?.toString() || '')
  const reviewScores =
    getReviewFiltersFromUrl(reviewScoreFilters)?.scores?.map((item) =>
      parseInt(item, 10),
    ) || []
  const currency =
    request.config.sessionSettings?.currency ||
    (siteDefinition.defaultCurrency as Currency)
  const shippingDestination =
    request.config.sessionSettings?.shippingDestination ||
    (defaultShippingDesintation.code as Country)

  if (skuNumber) {
    const pageTheme = await loadPageTheme<ProductPageLayout>({
      page: 'product',
      brand: siteDefinition.brand,
      subsite: siteDefinition.subsite,
    })

    const siteProperties = await loadSiteProperties(
      config,
      request.config.previewId,
    )

    const graphql = createGraphQLClient(config, request)

    const horizonFeatures = request.horizonFeatures || []

    const preselectVariant =
      (siteConfig.enablePreselectedProductVariant &&
        request.headers['x-enterprise-disable-preselected-variant'] !== '1') ??
      true

    const queryStartTime = new Date()

    // @TODO: REBUILD-8979: Create separate reviews page query & resolver
    const { data } = await graphql.query<
      { productPage: ProductPageData },
      ProductPageVariables
    >(REVIEWS_PAGE_QUERY_AURORA, {
      preselectVariant,
      currency,
      shippingDestination,
      sku: skuNumber,
      reviewsSort: sort,
      reviewsOffset: offset,
      clickAndCollectEnabled: isClickAndCollectEnabled(
        horizonFeatures?.includes(Feature.ClickAndCollect),
        siteConfig.enableClickAndCollect,
      ),
      notifyWhenInStockEnabled:
        horizonFeatures.includes(Feature.Waitlist) || false,
      reviewsFilter: { scores: reviewScores },
      reviewsLimit: MAX_REVIEWS_PER_PAGE,
      recommendation: ProductRecommendationType.FrequentlyBoughtTogether,
      recommendationLimit: 4,
      enableProductPersonalisation:
        siteConfig.enableProductPersonalisation || false,
    })

    const selectedOptions = getSelectedOptions(
      data.productPage.sortedOptionKeys,
      data.productPage.defaultSelectedChoices,
    )

    if (selectedOptions) {
      data.productPage.selectedOptions = selectedOptions
    }

    if (data && data.productPage) {
      const reviewsURL = data.productPage.url.replace('.html', '.reviews')
      const normalisedRequestUrl = request.browserUrl
        ?.split('?')[0]
        .replace(/^\/e2\//, '/')

      if (reviewsURL !== normalisedRequestUrl) {
        const redirectURL = new URL(
          reviewsURL,
          `https://${siteDefinition.domain}`,
        )
        Object.entries(request.query).forEach(([key, value]) => {
          if (typeof value === 'string') {
            redirectURL.searchParams.set(key, value)
          }
        })

        try {
          const histogram = getHistogram({
            name: 'enterprise_monitor_api_timings',
            labels: ['appname', 'brand', 'subsite', 'endpoint'],
            help: 'This metric stores the duration of API calls',
          })
          histogram
            .labels(
              'product',
              siteDefinition.brand,
              siteDefinition.subsite,
              'product_page_query',
            )
            .observe(new Date().getTime() - queryStartTime.getTime())
        } catch (error) {
          console.warn(
            `failed to raise metric for product page query: ${error}`,
          )
        }

        context.res.setHeader('Location', redirectURL.toString())
        context.res.writeHead(301)
        context.res.end()

        // Return empty props to honour the types
        return {
          props: {
            pageParams: {
              pageNumber: null,
              sortType: null,
              filter: null,
            },
          },
        }
      }

      return {
        cache: {
          ...(pageTTLConfig(CachePageType.REVIEWS) || {
            ttl: 300,
            grace: 86400,
          }),
          tags: [
            generateObjectCacheKey(
              ObjectCacheKey.PRODUCT,
              data.productPage.sku,
            ),
          ],
          vary: data.productPage.varyHeaders,
        },
        props: {
          pageParams: {
            ...results,
          },
          preselectVariant,
          pageTheme,
          options: { options: {}, choiceMap: {} },
          product: data.productPage,
          reviewFilters: reviewScoreFilters,
        },
      }
    }
  }

  const apollo = createApolloWithRequest(request, config, true)

  const { data: notFoundErrorWidgets } = await apollo.query<
    {
      componentWidgets: Maybe<Widget[]>
    },
    QueryPageArgs & ComponentWidgetsVariables
  >({
    query: WIDGET_QUERY,
    variables: {
      currency,
      shippingDestination,
      path: Routes.Product,
      name: ComponentName.PAGE_UNAVAILABLE,
    },
  })

  return {
    props: {
      pageParams: {
        notFoundPageWidgets: notFoundErrorWidgets.componentWidgets || [],
        pageNumber: null,
        sortType: null,
        reviewFilters: null,
      },
    },
  }
})

export default ProductReview
